import { Injectable, inject } from '@angular/core';
import { NativeService } from '@shared/services/native.service';
import { environment } from '@environments/environment';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let FB: any;

export interface FbStatusResponse {
  status: string;
  authResponse: FbAuthResponse;
}

export interface FbAuthResponse {
  accessToken: string;
  authenticationToken?: string;
  userID: string;
}

export interface FbNativeAuthResponse extends FbAuthResponse {
  error: boolean;
}

interface FacebookServiceStrategy {
  fetchLoginStatus(): Promise<FbAuthResponse>;

  doLogin(): Promise<FbAuthResponse>;

  logout(): Promise<void>;
}

class FacebookServiceJsStrategy implements FacebookServiceStrategy {
  private jsSdkInitialized: Promise<void>;

  constructor() {
    this.loadSdkAsync();
  }

  private loadSdkAsync(): void {
    this.jsSdkInitialized = this.initJsSdk();
    const s = 'script';
    const id = 'facebook-jssdk';
    const fjs = document.getElementsByTagName(s)[0];
    if (document.getElementById(id)) return;
    const js = document.createElement(s);
    js.id = id;
    js.src = '//connect.facebook.net/en_US/sdk.js';
    fjs.parentNode.insertBefore(js, fjs);
  }

  private initJsSdk(): Promise<void> {
    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (<any>window).fbAsyncInit = () => {
        FB.init({
          appId: environment.facebookAppId,
          status: true,
          cookie: true,
          xfbml: true,
          version: 'v19.0',
        });
        resolve(undefined);
      };
    });
  }

  async fetchLoginStatus(): Promise<FbAuthResponse> {
    await this.jsSdkInitialized;
    return new Promise((resolve) => {
      FB.getLoginStatus((response: FbStatusResponse) => {
        if (response.status === 'connected') {
          resolve(response.authResponse);
        } else {
          resolve(this.doLogin());
        }
      });
    });
  }

  async doLogin(): Promise<FbAuthResponse> {
    await this.jsSdkInitialized;
    return new Promise((resolve, reject) => {
      FB.login(
        async (response: FbStatusResponse) => {
          if (response.status === 'connected') {
            resolve(response.authResponse);
          } else {
            reject(response.status);
          }
        },
        { scope: 'public_profile,email' }
      );
    });
  }

  async logout(): Promise<void> {
    await this.jsSdkInitialized;
    return new Promise((resolve) => {
      try {
        FB.logout();
      } finally {
        resolve();
      }
    });
  }
}

class FacebookServiceNativeStrategy implements FacebookServiceStrategy {
  constructor(private nativeService: NativeService) {}

  fetchLoginStatus(): Promise<FbAuthResponse> {
    return this.doLogin();
  }

  async doLogin(): Promise<FbNativeAuthResponse> {
    const response = (await this.nativeService.communicationHandler.sendAndAwait(
      'fbLogin',
      {},
      null
    )) as FbNativeAuthResponse;
    if (response.error) {
      throw 'Unable to complete the native facebook login';
    }
    return response;
  }

  logout(): Promise<void> {
    return Promise.resolve();
  }
}

@Injectable({
  providedIn: 'root',
})
export class FacebookService implements FacebookServiceStrategy {
  private strategy: FacebookServiceStrategy;

  constructor() {
    const nativeService = inject(NativeService);

    if (!nativeService.isNative()) {
      this.strategy = new FacebookServiceJsStrategy();
    } else {
      this.strategy = new FacebookServiceNativeStrategy(nativeService);
    }
  }

  fetchLoginStatus(): Promise<FbAuthResponse> {
    return this.strategy.fetchLoginStatus();
  }

  doLogin(): Promise<FbAuthResponse> {
    return this.strategy.doLogin();
  }

  logout() {
    return this.strategy.logout();
  }
}
